import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Button } from '../common';
import { useKeycloakAuth } from '../../contexts/KeycloakAuthProvider';
import { useNotificationService } from '../Notification';
import { ConfirmDialog } from '../Notification/ConfirmDialog';
import { useSettingsTranslation } from '../../modules/i18n';

// GraphQL queries and mutations
const GET_USER_INFO = gql`
  query GetUserInfo($id: String) {
    findMyFirstUser(args: { where: { id: $id } }) {
      id
      email
      username
      firstName
      lastName
      role
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_USER_SETTINGS = gql`
  mutation UpdateUserSettings($id: String!, $firstName: String, $lastName: String) {
    updateUser(args: { where: { id: $id }, data: { firstName: $firstName, lastName: $lastName } }) {
      id
      email
      username
      firstName
      lastName
      role
      createdAt
      updatedAt
    }
  }
`;

interface FormValues {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
}

interface User {
  id: string;
  email: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
  updatedAt: string;
}

const UserProfile: React.FC = () => {
  const { t } = useSettingsTranslation();
  const { user: authUser } = useKeycloakAuth();

  // Validation schema - needs to be inside component to use t()
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, t('profile.validation.firstNameMin'))
      .max(30, t('profile.validation.firstNameMax'))
      .required(t('profile.validation.firstNameRequired')),
    lastName: Yup.string()
      .min(2, t('profile.validation.lastNameMin'))
      .max(30, t('profile.validation.lastNameMax'))
      .required(t('profile.validation.lastNameRequired')),
    email: Yup.string().email(t('profile.validation.emailInvalid')).required(t('profile.validation.emailRequired'))
  });

  // GraphQL queries
  const {
    data,
    loading: userLoading,
    refetch
  } = useQuery<{ findMyFirstUser: User | null }>(GET_USER_INFO, {
    variables: { id: authUser?.id },
    skip: !authUser?.id
  });

  const [updateUserSettings, { loading: updateLoading }] = useMutation(UPDATE_USER_SETTINGS);

  const user = data?.findMyFirstUser;

  const { showSuccess, showError } = useNotificationService();
  const [isMobile, setIsMobile] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [resetFormCallback, setResetFormCallback] = useState<(() => void) | null>(null);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const initialValues: FormValues = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    username: user?.username || '',
    email: user?.email || ''
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: any) => {
    if (!authUser?.id) {
      showError(t('profile.messages.authError'), t('profile.messages.authErrorDesc'));
      setSubmitting(false);
      return;
    }

    try {
      await updateUserSettings({
        variables: {
          id: authUser.id,
          firstName: values.firstName,
          lastName: values.lastName
        }
      });

      // Refetch user data to get updated info
      await refetch();

      showSuccess(t('profile.messages.updateSuccess'), t('profile.messages.updateSuccessDesc'));
    } catch (error) {
      console.error('Error updating profile:', error);
      showError(t('profile.messages.updateError'), t('profile.messages.updateErrorDesc'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm: () => void) => {
    setResetFormCallback(() => resetForm);
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    if (resetFormCallback) {
      resetFormCallback();
    }
    setShowCancelDialog(false);
    setResetFormCallback(null);
  };

  const handleCancelDialog = () => {
    setShowCancelDialog(false);
    setResetFormCallback(null);
  };

  // Show loading state
  if (userLoading) {
    return (
      <main className='flex h-full flex-col items-center justify-center border border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
        <div className='text-center'>
          <div className='mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary'></div>
          <p className='text-gray-600 dark:text-dark-text-secondary'>{t('profile.messages.loading')}</p>
        </div>
      </main>
    );
  }

  // Show error if no user data
  if (!user) {
    return (
      <main className='flex h-full flex-col items-center justify-center border border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
        <div className='text-center'>
          <p className='text-gray-600 dark:text-dark-text-secondary'>{t('profile.messages.loadError')}</p>
          <Button onClick={() => refetch()} className='mt-4' variant='outline'>
            {t('profile.actions.retry')}
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className='flex h-full flex-col border border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
      {/* Header - Fixed height - Hidden on mobile */}
      {!isMobile && (
        <div className='flex-shrink-0 border-b border-gray-200 p-4 dark:border-border-dark'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='flex-shrink-0 text-gray-500 dark:text-dark-text-secondary'>
                <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                  />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </div>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-dark-text'>{t('title')}</h2>
            </div>
          </div>
        </div>
      )}

      {/* Content - Scrollable area */}
      <div className='min-h-0 flex-1 overflow-y-auto scrollbar-thin'>
        <div className={isMobile ? 'p-0 pb-20' : 'p-4 sm:p-6 lg:p-8'}>
          <div className={isMobile ? 'w-full' : 'mx-auto max-w-4xl'}>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
              {({ isSubmitting, resetForm, dirty }) => (
                <Form className='flex h-full flex-col' id='user-profile-form'>
                  <div className='flex-1'>
                    <div className='overflow-hidden'>
                      <div className={isMobile ? 'px-4 py-6' : 'px-6 py-6'}>
                        {/* User Info Display */}
                        <div
                          className={`flex ${isMobile ? 'flex-col items-center text-center' : 'flex-col items-start sm:flex-row sm:items-center'} mb-6 space-y-4 sm:space-x-6 sm:space-y-0`}
                        >
                          <div className='relative'>
                            <div className='flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-primary to-primary-light'>
                              <svg className='h-10 w-10 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                                />
                              </svg>
                            </div>
                          </div>
                          <div className='flex-1'>
                            <h3 className='text-lg font-medium text-gray-900 dark:text-dark-text'>
                              {user?.firstName && user?.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user?.username || t('profile.labels.userInfo')}
                            </h3>
                            <p className='mt-1 text-sm text-gray-500 dark:text-dark-text-secondary'>{user?.email}</p>
                            <p className='text-sm text-gray-500 dark:text-dark-text-secondary'>
                              {t('profile.fields.role')}: {user?.role || t('profile.labels.roleUnknown')}
                            </p>
                          </div>
                        </div>

                        {/* Form Fields */}
                        <div className='space-y-6'>
                          <div>
                            <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
                              {t('profile.fields.lastName')} <span className='text-red-400'>*</span>
                            </label>
                            <Field
                              name='lastName'
                              type='text'
                              className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text'
                              placeholder={t('profile.placeholders.lastName')}
                            />
                            <ErrorMessage name='lastName' component='p' className='mt-1 text-sm text-red-600 dark:text-red-400' />
                          </div>

                          <div>
                            <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
                              {t('profile.fields.firstName')} <span className='text-red-400'>*</span>
                            </label>
                            <Field
                              name='firstName'
                              type='text'
                              className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text'
                              placeholder={t('profile.placeholders.firstName')}
                            />
                            <ErrorMessage name='firstName' component='p' className='mt-1 text-sm text-red-600 dark:text-red-400' />
                          </div>

                          <div>
                            <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
                              {t('profile.fields.username')}
                            </label>
                            <Field
                              name='username'
                              type='text'
                              readOnly
                              className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 shadow-sm dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
                              placeholder={t('profile.placeholders.username')}
                            />
                          </div>

                          <div>
                            <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
                              {t('profile.fields.email')} <span className='text-red-400'>*</span>
                            </label>
                            <Field
                              name='email'
                              type='email'
                              readOnly
                              className='w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-900 shadow-sm dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
                              placeholder={t('profile.placeholders.email')}
                            />
                            <ErrorMessage name='email' component='p' className='mt-1 text-sm text-red-600 dark:text-red-400' />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Desktop only */}
                  {!isMobile && (
                    <div className='mt-6 px-6'>
                      <div className='mx-auto max-w-4xl'>
                        <div className='flex justify-end space-x-3'>
                          <Button type='button' variant='outline' onClick={() => handleCancel(resetForm)} disabled={isSubmitting} className='w-24'>
                            {t('profile.actions.cancel')}
                          </Button>
                          <Button type='submit' disabled={isSubmitting || !dirty || updateLoading} className='w-24'>
                            {isSubmitting || updateLoading ? t('profile.actions.saving') : t('profile.actions.save')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Form>
              )}
            </Formik>

            {/* Fixed bottom buttons for mobile */}
            {isMobile && (
              <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                {({ resetForm, isSubmitting: _isSubmitting, dirty: _dirty }) => (
                  <div className='fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4 dark:border-border-dark dark:bg-dark-background-secondary'>
                    <div className='flex justify-between space-x-3'>
                      <Button type='button' variant='outline' onClick={() => handleCancel(resetForm)} className='flex-1'>
                        {t('profile.actions.cancel')}
                      </Button>
                      <Button type='submit' form='user-profile-form' className='flex-1'>
                        {t('profile.actions.save')}
                      </Button>
                    </div>
                  </div>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        title={t('profile.dialog.confirmCancel')}
        message={t('profile.dialog.confirmCancelMessage')}
        confirmText={t('profile.dialog.confirmCancelButton')}
        cancelText={t('profile.dialog.continueEditing')}
        confirmVariant='danger'
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelDialog}
      />
    </main>
  );
};

export { UserProfile };
export default UserProfile;
